const { default: cleanObject } = require('@/utils/cleanObject');
const { menuFormatter, productFormatter } = require('./formatters');
const axios = require('axios');
const { CMS_GATE_URL } = require('@common/config/gate.config');

const createToken = () => {
  const CancelToken = axios.CancelToken;
  return CancelToken.source();
}

const createApi = (Fetcher, store) => {
  /* COMMON */
  /**
   * шортхенд Axios.get для query params
   * @param url
   * @param params Объект с query параметрами
   */
  const get = (url, params = {}) => Fetcher({
    url,
    method: 'GET',
    params,
  });

  /**
   * шортхенд Axios.post для query params
   * @param url
   * @param data Тело запроса
   * @param params Объект с query параметрами
   */
  const post = (url, data = {}, params = {}, options = {}) => {
    return Fetcher({
      url,
      method: 'POST',
      data,
      params,
      ...options,
    });
  }

  // шортхенд для получения города
  const userCity = () => store.getters['user/cityId'];

  return {
    /* MAIN */
    loadMenu: (slots = []) =>
      new Promise((resolve, reject) => {
        get(`/menu/${slots.join()}`, { layout: 'desktop' }) // adaptive возвращает {desktop, mobile}
          .then(menuFormatter)
          .then(resolve)
          .catch(reject);
      }),

    getBanners: (slots = [], categoryUrl) =>
      get(`/banner/${slots.join()}`, {
        cityId: userCity(),
        categoryUrl,
      }),

    getStaticPage: (pageUrl = '') => get(`/static-pages/${pageUrl}`),

    getStaticFragment: (slotId = '', cityId = '') => get('/static/static-fragment', {
      cityId: cityId ? cityId : userCity(),
      slotId,
    }),

    loadRecentlyViewedProducts: () =>
      get('/products/recently-viewed', { cityId: userCity() }),

    loadRecommendationsProducts: (productIds = []) => {
      const tmpProductIds = productIds.map((productId) => {
        return `productIds=${productId}`;
      });
      const urlParams = [`cityId=${userCity()}`,...tmpProductIds]
        .join('&');

      return get(`/recommendations/products?${urlParams}`, {

      });
    },

    loadSlotProducts: (slot, categoryUrl) => {
      const slots = `slots=${slot}`;
      const urlParams = [`cityId=${userCity()}`, slots]
        .join('&');

      return get(`/recommendations/slots?${urlParams}`, {
        categoryUrl
      });
    },


    /* CATALOG */
    loadCategory: ({
      categoryUrl = '',
      cityId = userCity(),
      pageSize = 96,
      pageNumber = 0,
      sort = '',
      filters = '',
    } = {}) =>
      get(`/category/${categoryUrl}`, {
        cityId,
        pageSize,
        pageNumber,
        sort,
        filters,
      }),

    loadProduct: ({ productId = '', cityId = userCity() } = {}) => new Promise((resolve, reject) => {
      get(`/product/${productId}`, { cityId })
        .then(productFormatter)
        .then(resolve)
        .catch(reject);
    }),

    loadSizeTable: ({productId = '', cityId = userCity() } = {}) => get(`/product/${productId}/size-table`, { cityId }),

    loadProducts: ids =>
      get(`/products/${ids}?cityId=${userCity()}`),

    searchProducts: ({
      query = '',
      pageSize = 96,
      pageNumber = 0,
    } = {}) =>
      get('/products', {
        cityId: userCity(),
        query,
        pageSize,
        pageNumber,
      }),

    loadBreadCrumbs: ({ categoryUrl = '/' } = {}) =>
      get('/breadcrumbs', { categoryUrl }),


    /* GEO */
    detectCity: () => get('/city'),

    getCities: (ids = [userCity()]) =>
      get(`/cities/${ids}`),

    findCity: ({ query = '', size = 9 } = {}) =>
      get('/cities', { query, size }),

    loadStores: ({ cityId = userCity(), sort } = {}) =>
      get('/stores', { cityId, sort }),

    loadMicroCities: ({ cityId }) =>
      get('/cities/cluster',  cityId ),

    loadPopularCities: () =>
      get('/cities/popular'),

    loadMetroStations: ({ cityId = userCity() }) =>
      get('/metro-stations', { cityId }),

    findStreet: ({ query = '', cityId = userCity() } = {}) =>
      get('/streets', { query, cityId }),

    findHouses: ({ query = '', streetId } = {}) =>
      get('/houses', { query, streetId }),

    loadAvailabilityList: ({ productId = '', cityId = userCity() } = {}) =>
      get(`/product/${productId}/availability/stores`, { cityId }),

    /* CART/CHECKOUT */
    loadCart: ({ cityId = userCity() } = {}) =>
      get('/cart', { cityId }),

    addToCart: ({ items = [], storeId, shippingGroupId, cityId = userCity() }) =>
      post('/cart/products/add', cleanObject({
        items,
        storeId,
        shippingGroupId,
      }), { cityId }),

    removeFromCart: ({ lineIds = [] }) =>
      post('/cart/products/delete', { lineIds }),

    checkCartAvailability: ({ type, storeId, lineIds = [] } = {}) =>
      get(`/cart/stores/${type}/${storeId}/products`, { lineIds: lineIds.join() }),

    setShippingGroup: ({ type = 'unallocated', value, lineIds = [], cityId = userCity() } = {}) =>
      post('/cart/shipping-group', { type, value, lineIds }, { cityId }),

    getPickupPoints: ({ cityId, lineIds = [], sort }) =>
      get('/cart/stores', { cityId, lineIds: lineIds.join(), sort }),

    getStore: storeId =>
      get(`/store/${storeId}`),

    getThirdpartyStores: ids =>
      get(`/third-party-stores/${ids}`),

    saveDelivery: (form = {}) => {
      const source = createToken();
      const request = post('/cart/delivery', cleanObject(form), null, {
        cancelToken: source.token,
      });

      return { request, cancel: source.cancel };
    },

    setPaymentMethod: paymentMethod =>
      post('/cart/payment-method', { paymentMethod }),

    saveContact: ({ contact = {}, token = '' } = {}) => {
      const source = createToken();
      const request = post('/cart/contact', cleanObject({ contact, token }), null, {
        cancelToken: source.token,
      });

      return { request, cancel: source.cancel };
    },

    saveComment: ({ comment = '' } = {}) =>
      post('/cart/comment', { comment }),

    setPromo: promo => post('/cart/promo/add', { promo }),

    deletePromo: promo => post('/cart/promo/delete', { promo }),

    applyBonuses: apply => post('/cart/bonuses', { apply }),

    checkout: ({
      version = store.getters['cart/version'],
      subscribe = store.state.cart?.subscribe,
      communicationCode = '' } = {}) => {
        return post('cart/checkout', {
          version,
          subscribe,
          ...(communicationCode && {communicationCode}),
        })
      },

    /* ORDERS */
    getOrders: ({ pageNumber = 0, pageSize = 20 }) =>
      get('/orders', { pageNumber, pageSize }),

    getOrderDetails: ({ orderNumbers = [], cityId = userCity() }) =>
      get(`/orders/${orderNumbers.join()}`, { cityId }),

    getPaymentTools: ({ orderNumber = '', intentionNumber }) =>
      get(`/order/${orderNumber}/payment-tools`, { intentionNumber }),

    getCancelReasons: () =>
      get('/orders/cancel-reasons'),

    cancelOrder: ({ orderNumber = '', form = {} } = {}) =>
      post(`/order/${orderNumber}/cancel`, form),

    getPaymentLink: ({ orderNumber = '' } = {}) =>
      get(`/order/${orderNumber}/payment/acquiring`),

    getPaymentFpsQr: async ({ orderNumber = '' } = {}) => {
      const response = await get(`/order/${orderNumber}/payment-qr`);
      return {
        orderNumber,
        ...response,
      };
    },

    getPaymentFpsBanks: () => Fetcher({
      baseURL: 'https://qr.nspk.ru',
      url: '/proxyapp/c2bmembers.json',
    }),

    acquiringSuccess: ({ orderNumber = store.getters['cart/version'], aqcuiringParameters = '' } = {}) =>
      post(`/order/${orderNumber}/payment/acquiring/success`, { aqcuiringParameters }),

    /* ACCOUNT */
    accountExists: ({ type = 'phone', value, scanUnconfirmed = false }) =>
      post('/account/exists', { type, value, scanUnconfirmed }),

    changePhone: ({ phone = '', token = '' }) =>
      post('/account/phone', { phone, token }),

    register: form =>
      post('/registration', form),

    login: ({ type = 'sms', value = '', token = '' } = {}) =>
      post('/login', { type, value, token }),

    requestCode: ({ phone = '', email = '', channel = 'sms', token = '' } = {}) =>
      Fetcher({ baseURL: '/api', url: '/sms', method: 'POST', data: cleanObject({ phone, email, channel, token }),
    }),

    verifyCode: ({ phone = '', email, code = '' } = {}) =>
      post('/sms/verification', cleanObject({ phone, email, code })),

    getAccount: () => get('/account'),

    getLevel: () => get('/account/level'),

    updateAccount: ({ form } = {}) => Fetcher.put('/account', form),

    updateSubscription: ({ email } = {}) =>
      post('/subscriptions', { email }),

    changeSubscriptions: (subscriptions = []) =>
      post('/account/subscriptions', { subscriptions }),

    logout: () => post('/logout', {}),

    getCoupons: () => get('/account/coupons', { limit: 100 }),

    getPromotions: () => get('/account/promotions'),

    getPublications: (dateRange, pageSize) => get('/publication', {
      cityId: userCity(),
      sort: 'priority',
      dateFrom: dateRange?.from || '',
      dateTo: dateRange?.to || '',
      pageSize,
    }),

    getPublicationPage: id => get(`/publication/${id}`),

    loadFavoritesShort: () => get('/favorites/products/ids'),

    loadFavorites: () => new Promise((resolve, reject) => {
      get('/favorites/products',{
        cityId: userCity(),
      })
      .then(favorites =>
        favorites.map(f => ({
            ...f,
            models: f.models.map(productFormatter)
        }))
      )
      .then(resolve)
      .catch(reject)
    }),

    addToFavorites: ({ productId, skuId }) => Fetcher({
      url: `/favorites/products`,
      method: 'POST',
      params: cleanObject({
        productId,
        skuId,
        cityId: userCity(),
      }),
    }),

    removeFromFavorites: (favoriteId) => Fetcher({
      url: `favorites/products/${favoriteId}`,
      method: 'DELETE',
    }),

    loadBonusHistory: ({ dateFrom, dateTo } = {}) => {
      return get('/account/bonus-history', { dateFrom, dateTo });
    },


    /* ADDRESSES */
    getAddresses: () =>
      get('/account/addresses'),

    createAddress: ({ address = {} }) =>
      post('/account/addresses', cleanObject(address)),

    updateAddress: ({ address = {} }) =>
      Fetcher.put(`/account/addresses/${address.id}`, cleanObject(address)),

    removeAddress: ({ addressId = '' }) =>
      Fetcher.delete(`/account/addresses/${addressId}`),

    // reviews
    checkReviewsAvailable() {
      return Fetcher.get('/api/review-available',{
        baseURL: '/',
      });
    },

    getCountReviews({ productId } = {}) {
      return Fetcher.get(`/api/reviews-count/${productId}`,{
        baseURL: '/',
        validateStatus(status) {
          return status >= 200 && status < 300 || status === 422;
        },
      });
    },

    getReviews({ productId, page = 1, sort } = {}) {
      return Fetcher.get(`/api/reviews/${productId}`,{
        params: {
          page,
          sort,
        },
        baseURL: '/',
        validateStatus(status) {
          return status >= 200 && status < 300 || status === 422;
        },
      });
    },

    voteForReview({ reviewId, isLike, voterId }) {
      return Fetcher.post(`/api/review-vote/${reviewId}`, {
        isLike,
        voterId
       }, {
        baseURL: '/',
        validateStatus(status) {
          return status >= 200 && status < 300 || status === 422; // ответ с ошибкой уже проголосовавшего юзера
        },
      });
    },

    addReview: ({
        rating,
        fitRating,
        qualityRating,
        photoAccuracyRating,
        body,
        pros,
        cons,
        recommended,
        authorId,
        authorName,
        authorEmail,
        authorPhone,
        locationName,
        productId,
        dimensions,
        timeUsage,
      } = {}) =>  {
      return post(`/api/reviews/${productId}`, {
        rating,
        fitRating,
        qualityRating,
        photoAccuracyRating,
        productId,
        body,
        pros,
        cons,
        recommended,
        authorId,
        authorName,
        authorEmail,
        authorPhone,
        locationName,
        dimensions,
        timeUsage,
      },
      null,
      {
        baseURL: '/',
        validateStatus(status) {
          return status >= 200 && status < 300 || status === 422; // ответ с ошибкой уже проголосовавшего юзера
        },
      });
    },

    // csm
    addCsm: ({ wareId, price, discountPrice }) => {
      return Fetcher({
        url: `/add-to-csm`,
        method: 'POST',
        data: {
          wareId,
          price,
          discountPrice,
        },
        baseURL: '/',
      });
    },

    // resend email
    sendConfirmEmail({ email }) {
      return post('/account/reconfirmation/email', {}, {
        email
      });
    },

    changeEmail(email) {
      return post('/account/email', {
        email,
      }, {}, {
        validateStatus(status) {
          return status >= 200 && status < 300 || status === 400; // 400 - уже существует в системе
        },
      });
    },

    loadSeoOne({ url }) {
      return Fetcher({
        baseURL: `${CMS_GATE_URL}/api/v2`,
        url: `/seo/one`,
        method: 'GET',
        params: {
          entryPoint: 'funday_ecom_adaptive_ru',
          language: 'ru',
          absRefs: false,
          stag: false,
          url,
        },
      });
    },

    geoServiceDecode({ coder = 'YANDEX', searchString = '' } = {}) {
      return post('/geo-service/decode', {
        coder,
        searchString,
      }, {}, {
        baseURL: '/api'
      });
    },
  }
};

module.exports = createApi;
